import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import {
  getConsent,
  saveConsent,
  applyConsent,
  initConsentDefaults
} from '../lib/consent';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analiticos: false, marketing: false });

  // Boot: aplicar defaults e mostrar banner se ainda não existir consentimento
  useEffect(() => {
    initConsentDefaults();
    const existing = getConsent();
    if (existing) {
      applyConsent(existing);
      setPrefs({ analiticos: !!existing.analiticos, marketing: !!existing.marketing });
    } else {
      setShowBanner(true);
    }

    const openPrefs = () => {
      const cur = getConsent();
      if (cur) setPrefs({ analiticos: !!cur.analiticos, marketing: !!cur.marketing });
      setShowPrefs(true);
    };
    window.addEventListener('uf:open-cookie-preferences', openPrefs);
    return () => window.removeEventListener('uf:open-cookie-preferences', openPrefs);
  }, []);

  const handleAcceptAll = () => {
    saveConsent({ analiticos: true, marketing: true });
    setShowBanner(false);
    setShowPrefs(false);
  };

  const handleRejectAll = () => {
    saveConsent({ analiticos: false, marketing: false });
    setShowBanner(false);
    setShowPrefs(false);
  };

  const handleSavePrefs = () => {
    saveConsent({ analiticos: prefs.analiticos, marketing: prefs.marketing });
    setShowBanner(false);
    setShowPrefs(false);
  };

  const togglePref = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  if (!showBanner && !showPrefs) return null;

  return (
    <>
      {/* Banner principal */}
      {showBanner && !showPrefs && (
        <div
          role="dialog"
          aria-label="Consentimento de cookies"
          data-testid="cookie-banner"
          className="fixed bottom-0 left-0 right-0 z-[60] bg-black border-t border-yellow-400/40 shadow-2xl"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
              <p
                className="text-gray-200 flex-1"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 300,
                  fontSize: '0.92rem',
                  lineHeight: '1.6'
                }}
              >
                Utilizamos cookies para melhorar a sua experiência, analisar o tráfego do website e personalizar conteúdos. Pode aceitar todos os cookies, rejeitar ou configurar as suas preferências.{' '}
                <Link
                  to="/politica-de-privacidade"
                  className="text-yellow-400 hover:underline whitespace-nowrap"
                  data-testid="cookie-link-privacidade"
                >
                  Política de Privacidade
                </Link>
                .
              </p>

              <div className="flex flex-wrap items-center gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowPrefs(true)}
                  data-testid="cookie-btn-config"
                  className="uppercase px-5 py-3 border border-white/60 text-white hover:bg-white/10 transition-colors"
                  style={{
                    fontFamily: "'BebasNeue-Book', sans-serif",
                    fontSize: '15px',
                    letterSpacing: '0.06em',
                    borderRadius: 0
                  }}
                >
                  Configurar
                </button>
                <button
                  type="button"
                  onClick={handleRejectAll}
                  data-testid="cookie-btn-reject"
                  className="uppercase px-5 py-3 border border-white/60 text-white hover:bg-white/10 transition-colors"
                  style={{
                    fontFamily: "'BebasNeue-Book', sans-serif",
                    fontSize: '15px',
                    letterSpacing: '0.06em',
                    borderRadius: 0
                  }}
                >
                  Rejeitar
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  data-testid="cookie-btn-accept"
                  className="uppercase px-5 py-3 bg-yellow-400 hover:bg-yellow-500 text-black border border-yellow-400 transition-colors"
                  style={{
                    fontFamily: "'BebasNeue-Book', sans-serif",
                    fontSize: '15px',
                    letterSpacing: '0.06em',
                    borderRadius: 0
                  }}
                >
                  Aceitar todos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Painel de preferências */}
      {showPrefs && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Preferências de cookies"
          data-testid="cookie-prefs"
        >
          <div className="bg-black border border-yellow-400/40 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-800">
              <h3
                className="text-white uppercase"
                style={{
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  fontSize: '22px',
                  letterSpacing: '0.06em'
                }}
              >
                Preferências de cookies
              </h3>
              <button
                type="button"
                onClick={() => setShowPrefs(false)}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Fechar"
                data-testid="cookie-prefs-close"
                style={{ borderRadius: 0 }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="px-6 py-6 space-y-5">
              <p
                className="text-gray-300"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 300,
                  fontSize: '0.9rem',
                  lineHeight: '1.6'
                }}
              >
                Escolha quais categorias de cookies pretende permitir. Os cookies necessários são essenciais para o funcionamento do website e estão sempre ativos.
              </p>

              {/* Necessários */}
              <CategoryRow
                title="Necessários"
                description="Essenciais para o funcionamento básico do website. Não podem ser desativados."
                checked
                disabled
              />

              {/* Analíticos */}
              <CategoryRow
                title="Analíticos (Google Analytics)"
                description="Permitem analisar o tráfego e melhorar o website."
                checked={prefs.analiticos}
                onToggle={() => togglePref('analiticos')}
                testid="cookie-toggle-analiticos"
              />

              {/* Marketing */}
              <CategoryRow
                title="Marketing"
                description="Reservado para futuras campanhas. Atualmente não estão ativos quaisquer cookies de marketing."
                checked={prefs.marketing}
                onToggle={() => togglePref('marketing')}
                testid="cookie-toggle-marketing"
              />

              <p className="text-xs text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                Saiba mais na nossa{' '}
                <Link to="/politica-de-privacidade" className="text-yellow-400 hover:underline">Política de Privacidade</Link>.
              </p>
            </div>

            <div className="px-6 py-5 border-t border-zinc-800 flex flex-wrap gap-3 justify-end">
              <button
                type="button"
                onClick={handleRejectAll}
                data-testid="cookie-prefs-reject"
                className="uppercase px-5 py-3 border border-white/60 text-white hover:bg-white/10 transition-colors"
                style={{
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  fontSize: '14px',
                  letterSpacing: '0.06em',
                  borderRadius: 0
                }}
              >
                Rejeitar
              </button>
              <button
                type="button"
                onClick={handleSavePrefs}
                data-testid="cookie-prefs-save"
                className="uppercase px-5 py-3 bg-yellow-400 hover:bg-yellow-500 text-black border border-yellow-400 transition-colors"
                style={{
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  fontSize: '14px',
                  letterSpacing: '0.06em',
                  borderRadius: 0
                }}
              >
                Guardar preferências
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const CategoryRow = ({ title, description, checked, onToggle, disabled, testid }) => (
  <div className="flex items-start gap-4 p-4 border border-zinc-800 hover:border-zinc-700 transition-colors">
    <div className="flex-1">
      <p
        className="text-white mb-1"
        style={{
          fontFamily: "'BebasNeue-Book', sans-serif",
          fontSize: '17px',
          letterSpacing: '0.04em'
        }}
      >
        {title}
      </p>
      <p
        className="text-gray-400"
        style={{
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 300,
          fontSize: '0.82rem',
          lineHeight: '1.55'
        }}
      >
        {description}
      </p>
    </div>
    <label className="inline-flex items-center cursor-pointer pt-1">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        data-testid={testid}
      />
      <span
        className={`relative w-11 h-6 transition-colors ${
          disabled ? 'bg-yellow-400/40 cursor-not-allowed' : checked ? 'bg-yellow-400' : 'bg-zinc-700'
        }`}
        style={{ borderRadius: 0 }}
      >
        <span
          className="absolute top-0.5 left-0.5 w-5 h-5 bg-black transition-transform"
          style={{ borderRadius: 0, transform: checked ? 'translateX(20px)' : 'translateX(0)' }}
        />
      </span>
    </label>
  </div>
);

export default CookieConsent;
